'use client';

import Link from "next/link";
import Image from "next/image";
import type { CaseItem } from "@/lib/contentful-types";
import { Placeholder } from "@/components/taller/placeholder";
import { useTranslations } from "@/i18n/client";
import { Reveal } from "@/components/ui/reveal";

const CASOS_ROOT = "/casos-exito";

interface CasosPreviewProps {
  cases?: CaseItem[];
}

const numberToWord = (n: number, locale: string) => {
  const es = ['Cero', 'Un', 'Dos', 'Tres', 'Cuatro', 'Cinco', 'Seis', 'Siete', 'Ocho', 'Nueve', 'Diez'];
  const en = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
  const words = locale === 'es' ? es : en;
  return words[n] || n.toString();
};

/** § 06 — Casos preview. Two-column asymmetric grid with staggered Y offset. */
export function CasosPreview({ cases = [] }: CasosPreviewProps) {
  const { t, locale, isLoading } = useTranslations();
  const cDict = t('casos');

  if (isLoading) return null;

  const totalCount = cases.length;
  // Mostramos los primeros 4 (los mejores según orden de Contentful)
  const items = cases.slice(0, 4);
  if (items.length === 0) return null;

  const countWord = numberToWord(totalCount, locale);

  return (
    <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-[120px]">
      <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-6">
        <div className="flex flex-col sm:grid sm:grid-cols-[80px_1fr] gap-4 sm:gap-6 flex-1">
          <div />
          <div>
            <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)]">
              {cDict.overline || 'Trabajo reciente'}
            </div>
            <h2 className="font-sans font-[350] text-[28px] sm:text-[36px] md:text-[52px] leading-[1.05] tracking-[-0.02em] text-[color:var(--color-ink)] mt-3 mb-0">
              {countWord} {cDict.heading || 'proyectos. '}
              <em className="italic text-[color:var(--color-accent)]">
                {cDict.headingAccent || 'Cero pagos adelantados.'}
              </em>
            </h2>
          </div>
        </div>
        <Link
          href={CASOS_ROOT}
          className="font-mono text-[11px] tracking-[0.14em] uppercase text-[color:var(--color-ink)] border-b-[1.5px] border-[color:var(--color-accent)] pb-1 no-underline self-start md:self-auto"
        >
          {cDict.cta || 'Ver todos →'}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {items.map((c, i) => {
          const img = c.imagenSecundaria || c.imagenPrincipal;
          
          return (
            <Reveal key={c.client} delay={i * 0.1} width="100%">
              <Link
                href={`${CASOS_ROOT}/${c.slug}`}
                className={`bg-[color:var(--color-surface)] rounded-lg overflow-hidden border border-[color:var(--color-border)] no-underline text-[color:var(--color-ink)] block hover:border-[color:var(--color-accent)] transition-colors ${i % 2 ? "md:translate-y-10" : ""
                  }`}
              >
                <div className="relative overflow-hidden group/img bg-black/10" style={{ height: c.tall || 320 }}>
                  {img ? (
                    <Image
                      src={img.url}
                      alt={img.alt || c.client}
                      fill
                      className="object-contain p-4 transition-transform duration-700 group-hover/img:scale-105"
                      sizes="(max-width: 768px) 100vw, 50vw"
                    />
                  ) : (
                    <Placeholder label={c.client} height="100%" />
                  )}
                  <div className="absolute inset-0 bg-black/5 group-hover/img:bg-transparent transition-colors duration-500" />
                </div>
                
                <div className="px-6 pt-[22px] pb-[26px] flex justify-between items-baseline gap-4">
                  <div>
                    <div className="font-sans text-[24px] tracking-[-0.015em] text-[color:var(--color-ink)]">
                      {c.client}
                    </div>
                    <div className="font-sans text-[13px] text-[color:var(--color-mute)] mt-[3px]">
                      {c.kind}
                    </div>
                  </div>
                  {c.tag && (
                    <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--color-accent)] text-right shrink-0">
                      {c.tag}
                    </div>
                  )}
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
